/**
 * Can-checkout gate: fetches API, caches in localStorage (12h or campaign date).
 * When blocked: disables checkout + add-to-cart, hides accelerated checkout, shows message.
 */
import { ThemeEvents } from '@theme/events';

const CAN_CHECKOUT_API_BASE =
  typeof window !== 'undefined' && document.documentElement.dataset.canCheckoutApiBase
    ? document.documentElement.dataset.canCheckoutApiBase
    : 'https://sarris-fundraising-dashboard-ashen.vercel.app';

const CACHE_KEY = 'can_checkout';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

const CHECKOUT_BUTTON_SELECTOR = 'button[name="checkout"], #checkout, .cart__checkout-button';
const ADD_TO_CART_BUTTON_SELECTOR =
  'button[name="add"], .add-to-cart-button, .quick-add__button, .quick-add__button--add';
const BLOCKED_BUTTON_SELECTOR = `${CHECKOUT_BUTTON_SELECTOR}, ${ADD_TO_CART_BUTTON_SELECTOR}`;

/** @type {{ can_checkout: boolean; message?: string; period?: string; campaign_end_date?: string; last_campaign_end_date?: string; next_campaign_start_date?: string } | null} */
let canCheckoutResult = null;

const FUNDRAISER_CODE_KEY = 'active_fundraiser_code';

function hasValidatedFundraiserCode() {
  try {
    const raw = localStorage.getItem(FUNDRAISER_CODE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!(parsed && parsed.id);
  } catch {
    return false;
  }
}

function isBlocked() {
  if (canCheckoutResult === null) return false;
  return !canCheckoutResult.can_checkout || !hasValidatedFundraiserCode();
}

/** @param {string} dateStr */
function formatDateForDisplay(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const d = new Date(dateStr.trim());
  if (isNaN(d.getTime())) return dateStr;
  const locale = document.documentElement?.lang?.replace(/_/g, '-') || 'en-US';
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric' }).format(d);
}

/** @param {string} str @param {boolean} [endOfDay] */
function parseExpiryDate(str, endOfDay = false) {
  if (!str || typeof str !== 'string') return NaN;
  const s = str.trim();
  if (s.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(s + 'T00:00:00.000Z');
    if (isNaN(d.getTime())) return NaN;
    if (endOfDay) return d.getTime() + 24 * 60 * 60 * 1000 - 1;
    return d.getTime();
  }
  return new Date(s).getTime();
}

/** @param {{ can_checkout: boolean; campaign_end_date?: string; next_campaign_start_date?: string }} result */
function computeExpiresAt(result) {
  const now = Date.now();
  const maxExpires = now + CACHE_TTL_MS;
  if (result.can_checkout && result.campaign_end_date) {
    const ms = parseExpiryDate(result.campaign_end_date, true);
    if (!isNaN(ms) && ms > now && ms < maxExpires) return ms;
  }
  if (!result.can_checkout && result.next_campaign_start_date) {
    const ms = parseExpiryDate(result.next_campaign_start_date, false);
    if (!isNaN(ms) && ms > now && ms < maxExpires) return ms;
  }
  return maxExpires;
}

function getCachedCanCheckout() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (typeof stored._expiresAt !== 'number' || Date.now() >= stored._expiresAt) return null;
    return {
      can_checkout: stored.can_checkout === true,
      message: stored.message,
      period: stored.period,
      campaign_end_date: stored.campaign_end_date,
      last_campaign_end_date: stored.last_campaign_end_date,
      next_campaign_start_date: stored.next_campaign_start_date,
    };
  } catch {
    return null;
  }
}

/** @param {{ can_checkout: boolean; [key: string]: unknown }} result */
function setCachedCanCheckout(result) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...result, _expiresAt: computeExpiresAt(result) }));
  } catch {}
}

async function fetchCanCheckout() {
  const res = await fetch(`${CAN_CHECKOUT_API_BASE}/api/campaigns/can-checkout?t=${Date.now()}`, {
    credentials: 'omit',
  });
  if (!res.ok) return { can_checkout: true };
  const data = await res.json();
  return {
    can_checkout: data.can_checkout === true,
    message: data.message,
    period: data.period,
    campaign_end_date: data.campaign_end_date,
    last_campaign_end_date: data.last_campaign_end_date,
    next_campaign_start_date: data.next_campaign_start_date,
  };
}

/** @param {MouseEvent} e */
function handleBlockedClick(e) {
  if (!isBlocked()) return;
  const target = e.target instanceof Element ? e.target : null;
  const btn = target?.closest(BLOCKED_BUTTON_SELECTOR);
  if (btn) {
    e.preventDefault();
    e.stopPropagation();
    btn.classList.remove('atc-added');
    setTimeout(() => btn.classList.remove('atc-added'), 0);
  }
}

/** @param {Event} e */
function handleBlockedSubmit(e) {
  if (!isBlocked()) return;
  const form = e.target instanceof HTMLFormElement ? e.target : null;
  if (!form) return;
  if (form.querySelector(BLOCKED_BUTTON_SELECTOR) || form.matches('[data-type="add-to-cart-form"]')) {
    e.preventDefault();
    form.querySelectorAll(BLOCKED_BUTTON_SELECTOR).forEach((el) => el.classList.remove('atc-added'));
  }
}

function applyCanCheckoutState() {
  if (canCheckoutResult === null || !isBlocked()) return;

  if (blockedStateObserver) blockedStateObserver.disconnect();

  // Only the campaign-closed gate shows a message. The missing-ID gate blocks silently
  // (the validation modal, opened on checkout-button click, is the discoverability path).
  const showMessage = !canCheckoutResult.can_checkout;

  const { message, period, last_campaign_end_date, next_campaign_start_date } = canCheckoutResult;
  const nextFormatted = next_campaign_start_date ? formatDateForDisplay(next_campaign_start_date) : '';
  const lastFormatted = last_campaign_end_date ? formatDateForDisplay(last_campaign_end_date) : '';

  const inferredPeriod =
    period === 'offseason' || period === 'campaign_ended'
      ? period
      : last_campaign_end_date && next_campaign_start_date
        ? 'campaign_ended'
        : next_campaign_start_date
          ? 'offseason'
          : null;

  let displayMessage;
  if (inferredPeriod === 'offseason' && nextFormatted) {
    displayMessage = `Fundraising is currently closed. The next campaign season begins ${nextFormatted}.`;
  } else if (inferredPeriod === 'campaign_ended' && lastFormatted && nextFormatted) {
    displayMessage = `This campaign has ended on ${lastFormatted}. The next campaign starts ${nextFormatted}.`;
  } else {
    displayMessage = message?.trim() || 'Checkout is not available at this time.';
    if (lastFormatted || nextFormatted) {
      const parts = [];
      if (lastFormatted) parts.push(`Last campaign ended: ${lastFormatted}`);
      if (nextFormatted) parts.push(`Next campaign starts: ${nextFormatted}`);
      displayMessage += ' ' + parts.join('. ');
    }
  }

  document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR).forEach((el) => {
    if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
      el.disabled = true;
      el.setAttribute('disabled', '');
      el.setAttribute('aria-disabled', 'true');
    }
  });

  document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.hidden = true;
      el.style.display = 'none';
    }
  });
  document.querySelectorAll('shopify-accelerated-checkout-cart, shopify-accelerated-checkout').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.hidden = true;
      el.style.display = 'none';
    }
  });
  document.querySelectorAll('.accelerated-checkout-block').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.hidden = true;
      el.style.display = 'none';
    }
  });

  if (showMessage) {
    document.querySelectorAll('.cart__ctas').forEach((ctas) => {
      if (!(ctas instanceof HTMLElement)) return;
      let msgEl = ctas.querySelector('.cart-checkout-blocked-message');
      if (!msgEl) {
        msgEl = document.createElement('p');
        msgEl.className = 'cart-checkout-blocked-message';
        msgEl.setAttribute('aria-live', 'polite');
        msgEl.setAttribute('role', 'status');
        ctas.prepend(msgEl);
      }
      msgEl.textContent = displayMessage;
      if (msgEl instanceof HTMLElement) msgEl.hidden = false;
    });
  }

  const addToCartButtons = new Set(document.querySelectorAll(ADD_TO_CART_BUTTON_SELECTOR));
  addToCartButtons.forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.disabled = true;
    btn.setAttribute('disabled', '');
    btn.setAttribute('aria-disabled', 'true');
    if (!showMessage) return;
    const isPDP = !btn.closest('quick-add-component') && !btn.closest('.product-card');
    if (!isPDP) return;
    let msgEl = btn.nextElementSibling?.classList?.contains('add-to-cart-blocked-message')
      ? btn.nextElementSibling
      : null;
    if (!msgEl) {
      msgEl = document.createElement('p');
      msgEl.className = 'add-to-cart-blocked-message';
      msgEl.setAttribute('aria-live', 'polite');
      msgEl.setAttribute('role', 'status');
      btn.after(msgEl);
    }
    msgEl.textContent = displayMessage;
    if (msgEl instanceof HTMLElement) msgEl.hidden = false;
  });

  if (showMessage) ensureAddToCartBlockedMessageStyle();
  startBlockedStateObserver();
  if (blockedStateObserver) {
    blockedStateObserver.observe(document.body, { childList: true, subtree: true });
  }
}

let addToCartBlockedMessageStyleInjected = false;
function ensureAddToCartBlockedMessageStyle() {
  if (addToCartBlockedMessageStyleInjected) return;
  addToCartBlockedMessageStyleInjected = true;
  const style = document.createElement('style');
  style.textContent =
    '.add-to-cart-blocked-message{margin:.25rem 0 0;font-size:0.8125rem;color:rgb(var(--color-foreground-rgb)/0.85);line-height:1.3;}';
  document.head.appendChild(style);
}

let blockedStateObserverStarted = false;
/** @type {MutationObserver | null} */
let blockedStateObserver = null;
function startBlockedStateObserver() {
  if (blockedStateObserverStarted || !isBlocked()) return;
  blockedStateObserverStarted = true;
  let raf = 0;
  blockedStateObserver = new MutationObserver(() => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      if (isBlocked()) applyCanCheckoutState();
    });
  });
  blockedStateObserver.observe(document.body, { childList: true, subtree: true });
}

function onCartUpdate() {
  requestAnimationFrame(() => requestAnimationFrame(applyCanCheckoutState));
}

function clearBlockedState() {
  if (blockedStateObserver) {
    blockedStateObserver.disconnect();
    blockedStateObserver = null;
    blockedStateObserverStarted = false;
  }
  document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR).forEach((el) => {
    if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
      el.disabled = false;
      el.removeAttribute('disabled');
      el.removeAttribute('aria-disabled');
    }
  });
  document.querySelectorAll(ADD_TO_CART_BUTTON_SELECTOR).forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.disabled = false;
    btn.removeAttribute('disabled');
    btn.removeAttribute('aria-disabled');
  });
  document
    .querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart, shopify-accelerated-checkout, .accelerated-checkout-block')
    .forEach((el) => {
      if (el instanceof HTMLElement) {
        el.hidden = false;
        el.style.removeProperty('display');
      }
    });
}

function onValidationChanged() {
  if (isBlocked()) {
    applyCanCheckoutState();
  } else {
    clearBlockedState();
  }
}

function init() {
  document.addEventListener('click', handleBlockedClick, true);
  document.addEventListener('submit', handleBlockedSubmit, true);
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  document.addEventListener('fundraising:validation-changed', onValidationChanged);

  const cached = getCachedCanCheckout();
  if (cached !== null) {
    canCheckoutResult = cached;
    applyCanCheckoutState();
    return;
  }

  fetchCanCheckout()
    .then((result) => {
      canCheckoutResult = result;
      setCachedCanCheckout(result);
      applyCanCheckoutState();
    })
    .catch(() => {
      canCheckoutResult = { can_checkout: true };
      setCachedCanCheckout({ can_checkout: true });
      applyCanCheckoutState();
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
